import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Select } from 'antd';
import { VehicleTypePageType } from '../types/VehicleModelType';
import EditableDrawer from 'components/EditableDrawer';
import { useTranslation } from 'react-i18next';

interface StatusOption {
    label: string;
    value: number;
}

interface VehicleModelFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: VehicleTypePageType) => void;
    initialValues?: Partial<VehicleTypePageType>;
    mode: 'create' | 'edit';
    statusOptions: StatusOption[];
    vehicleTypes: VehicleTypePageType[];
}

const inputStyle = { width: 240 };

const VehicleModelFormDrawer: React.FC<VehicleModelFormDrawerProps> = ({
    visible,
    onClose,
    onSubmit,
    initialValues,
    mode,
    statusOptions,
    vehicleTypes,
}) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { t } = useTranslation();

    const makeUniquenessValidator = (
        field: keyof Pick<VehicleTypePageType, 'nameEn' | 'nameZh' | 'nameEs'>,
        messageKey: string
    ) => ({
        validator(_: unknown, value: string) {
            if (!value) return Promise.resolve();
            const conflict = vehicleTypes.find(vt => vt[field] === value);
            if (conflict && conflict.id !== initialValues?.id) {
                return Promise.reject(new Error(t(messageKey) as string));
            }
            return Promise.resolve();
        },
    });

    useEffect(() => {
        if (visible) {
            form.resetFields();
            form.setFieldsValue({ status: 1, ...initialValues });
        }
    }, [visible, initialValues, form]);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const values = await form.validateFields();
            await onSubmit(values);
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const drawerTitle = mode === 'create' ? t('central.vehicleModel.pageDetails.new') : t('central.vehicleModel.pageDetails.edit');

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            confirmText={isSubmitting ? t('components.button.processing') as string : t('components.button.confirm') as string}
            cancelText={t('components.button.cancel') as string}
            confirmDisabled={isSubmitting}
            width={500}
        >
            <Form
                form={form}
                layout="horizontal"
                labelAlign="left"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                initialValues={{ status: 1, ...initialValues }}
            >
                <Form.Item
                    name="nameZh"
                    label={t('central.vehicleModel.pageDetails.nameZh')}
                    normalize={(v: string) => v?.trim()}
                    rules={[
                        { required: true, message: t('central.vehicleModel.messages.pleaseInputNameZh') as string },
                        makeUniquenessValidator('nameZh', 'central.vehicleModel.messages.nameZhAlreadyExists'),
                    ]}
                >
                    <Input placeholder={t('central.vehicleModel.placeholders.inputNameZh') as string} style={inputStyle} maxLength={64} />
                </Form.Item>
                <Form.Item
                    name="nameEn"
                    label={t('central.vehicleModel.pageDetails.nameEn')}
                    normalize={(v: string) => v?.trim()}
                    rules={[
                        { required: true, message: t('central.vehicleModel.messages.pleaseInputNameEn') as string },
                        makeUniquenessValidator('nameEn', 'central.vehicleModel.messages.nameEnAlreadyExists'),
                    ]}
                >
                    <Input placeholder={t('central.vehicleModel.placeholders.inputNameEn') as string} style={inputStyle} maxLength={64} />
                </Form.Item>
                <Form.Item
                    name="nameEs"
                    label={t('central.vehicleModel.pageDetails.nameEs')}
                    normalize={(v: string) => v?.trim()}
                    rules={[
                        { required: true, message: t('central.vehicleModel.messages.pleaseInputNameEs') as string },
                        makeUniquenessValidator('nameEs', 'central.vehicleModel.messages.nameEsAlreadyExists'),
                    ]}
                >
                    <Input placeholder={t('central.vehicleModel.placeholders.inputNameEs') as string} style={inputStyle} maxLength={64} />
                </Form.Item>
                <Form.Item name="defaultMaxWeightLbs" label={t('central.vehicleModel.pageDetails.payload')} rules={[{ required: true, type: "number", message: t('central.vehicleModel.messages.pleaseInputPayload') as string }]}>
                    <InputNumber min={0.01} max={99999999.99} precision={2} style={inputStyle} placeholder={t('central.vehicleModel.placeholders.inputPayload') as string} />
                </Form.Item>
                <Form.Item name="defaultMaxVolumeFt3" label={t('central.vehicleModel.pageDetails.capacity')} rules={[{ required: true, type: "number", message: t('central.vehicleModel.messages.pleaseInputCapacity') as string }]}>
                    <InputNumber min={0.01} max={9999999.999} precision={3} style={inputStyle} placeholder={t('central.vehicleModel.placeholders.inputCapacity') as string} />
                </Form.Item>
                <Form.Item name="defaultLengthFt" label={t('central.vehicleModel.pageDetails.length')} rules={[{ required: true, type: "number", message: t('central.vehicleModel.messages.pleaseInputLength') as string }]}>
                    <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('central.vehicleModel.placeholders.inputLength') as string} />
                </Form.Item>
                <Form.Item name="defaultWidthFt" label={t('central.vehicleModel.pageDetails.width')} rules={[{ required: true, type: "number", message: t('central.vehicleModel.messages.pleaseInputWidth') as string }]}>
                    <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('central.vehicleModel.placeholders.inputWidth') as string} />
                </Form.Item>
                <Form.Item name="defaultHeightFt" label={t('central.vehicleModel.pageDetails.height')} rules={[{ required: true, type: "number", message: t('central.vehicleModel.messages.pleaseInputHeight') as string }]}>
                    <InputNumber min={0.01} max={999999.99} precision={2} style={inputStyle} placeholder={t('central.vehicleModel.placeholders.inputHeight') as string} />
                </Form.Item>
                <Form.Item name="status" label={t('central.vehicleModel.pageDetails.status')} rules={[{ required: true, message: t('central.vehicleModel.messages.pleaseSelectStatus') as string }]}>
                    <Select options={statusOptions} placeholder={t('central.vehicleModel.placeholders.select') as string} style={inputStyle} defaultValue={1} />
                </Form.Item>
            </Form>
        </EditableDrawer>
    );
};

export default VehicleModelFormDrawer;
